import React, { useState } from 'react'
import PropTypes from 'prop-types'
import clsx from 'clsx'
import Box from '@material-ui/core/Box'
import ButtonBase from '@material-ui/core/ButtonBase'
import Typography from '@material-ui/core/Typography'
import ChevronRightIcon from 'components/material-symbol/icons/ChevronRight'
import ExpandMoreIcon from 'components/material-symbol/icons/ExpandMore'
import EditIcon from 'components/material-symbol/icons/Edit'
import MonitoringIcon from 'components/material-symbol/icons/Monitoring'
import SectionEmptyState from 'components/section-empty-state'
import TrendingUpIcon from 'components/material-symbol/icons/TrendingUp'
import { BenchmarkDeltaChip } from 'pages/pga-coach/dashboard/performance-benchmarks/benchmark-chips'
import { BENCHMARK_PALETTE } from 'pages/pga-coach/dashboard/dashboard-colors'
import { SettingsSection } from '../settings-components'
import useSettingsStyles from '../settings.styles'
import { resultTitle } from './constants'
import { MANUAL_ICON, RULE_ICONS } from './rule-icons'
import useStyles from './styles'

const REPLIED_FILL = BENCHMARK_PALETTE.info.scaleFill
const BOOKED_FILL = BENCHMARK_PALETTE.success.scaleFill
const BASELINE_FILL = BENCHMARK_PALETTE.neutral.marker

const rate = (part, whole) =>
  whole > 0 ? Math.round((part / whole) * 100) : null

const formatRate = value => (value === null ? '—' : `${value}%`)

const studentCount = count => `${count} ${count === 1 ? 'student' : 'students'}`

const groupShape = PropTypes.shape({
  count: PropTypes.number.isRequired,
  repliedCount: PropTypes.number,
  bookedCount: PropTypes.number.isRequired,
})

function RateBlock({ label, part, whole, color, captionSuffix = '' }) {
  const classes = useStyles()
  const value = rate(part, whole)

  return (
    <Box className={classes.rateBlock}>
      <Typography component="p" className={classes.rateLabel}>
        {label}
      </Typography>
      <Typography component="p" className={classes.rateValue}>
        {formatRate(value)}
      </Typography>
      <Box
        className={classes.rateTrack}
        role="progressbar"
        aria-label={label}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={value ?? 0}
      >
        <Box
          className={classes.rateFill}
          style={{ width: `${value ?? 0}%`, backgroundColor: color }}
        />
      </Box>
      <Typography component="p" className={classes.rateCaption}>
        {part} of {studentCount(whole)}
        {captionSuffix}
      </Typography>
    </Box>
  )
}

RateBlock.propTypes = {
  label: PropTypes.string.isRequired,
  part: PropTypes.number.isRequired,
  whole: PropTypes.number.isRequired,
  color: PropTypes.string.isRequired,
  captionSuffix: PropTypes.string,
}

/**
 * Collapsible row, same pattern as the performance benchmarks list: the header
 * carries the headline number, the body holds the detail.
 */
function ResultItem({
  testId,
  icon,
  title,
  description,
  chip,
  expandable = true,
  children,
}) {
  const classes = useStyles()
  const [expanded, setExpanded] = useState(false)
  const bodyId = `${testId}-body`
  const open = expandable && expanded

  const header = (
    <>
      <Box className={classes.resultHeaderLeft}>
        <Box className={classes.resultIcon} aria-hidden>
          {icon}
        </Box>
        <Box className={classes.resultHeaderText}>
          <Typography component="p" className={classes.resultTitle}>
            {title}
          </Typography>
          <Typography component="p" className={classes.resultDescription}>
            {description}
          </Typography>
        </Box>
      </Box>
      <Box className={classes.resultHeaderRight}>
        {chip}
        {expandable ? (
          open ? (
            <ExpandMoreIcon className={classes.resultChevron} aria-hidden />
          ) : (
            <ChevronRightIcon className={classes.resultChevron} aria-hidden />
          )
        ) : null}
      </Box>
    </>
  )

  return (
    <Box className={classes.resultItem} data-testid={testId}>
      {expandable ? (
        <ButtonBase
          component="div"
          className={clsx(classes.resultHeader, {
            [classes.resultHeaderExpanded]: open,
          })}
          onClick={() => setExpanded(current => !current)}
          aria-expanded={open}
          aria-controls={open ? bodyId : undefined}
        >
          {header}
        </ButtonBase>
      ) : (
        <Box className={classes.resultHeader}>{header}</Box>
      )}
      {open ? (
        <Box className={classes.resultBody} id={bodyId}>
          {children}
        </Box>
      ) : null}
    </Box>
  )
}

ResultItem.propTypes = {
  testId: PropTypes.string.isRequired,
  icon: PropTypes.node.isRequired,
  title: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
  chip: PropTypes.node,
  expandable: PropTypes.bool,
  children: PropTypes.node,
}

const liftLabel = lift => {
  if (lift > 0) return `+${lift} pts`
  if (lift < 0) return `${lift} pts`
  return 'No difference'
}

export function Comparison({
  testId,
  icon,
  label,
  first,
  second,
  firstLabel,
  secondLabel,
  minSize,
}) {
  const ready = first.count >= minSize && second.count >= minSize

  if (!ready) {
    return (
      <ResultItem
        testId={testId}
        icon={icon}
        title={label}
        description={`Shows once each group has ${minSize} students (now ${first.count} and ${second.count}).`}
        expandable={false}
      />
    )
  }

  const firstRate = rate(first.bookedCount, first.count)
  const secondRate = rate(second.bookedCount, second.count)
  const lift = firstRate - secondRate

  return (
    <ResultItem
      testId={testId}
      icon={icon}
      title={label}
      description={`${firstLabel} ${formatRate(
        firstRate,
      )} booked vs. ${secondLabel.toLowerCase()} ${formatRate(secondRate)}`}
      chip={
        <BenchmarkDeltaChip
          label={liftLabel(lift)}
          positive={lift > 0}
          neutral={lift === 0}
        />
      }
    >
      <RateBlock
        label={firstLabel}
        captionSuffix=" booked"
        part={first.bookedCount}
        whole={first.count}
        color={lift >= 0 ? BOOKED_FILL : BASELINE_FILL}
      />
      <RateBlock
        label={secondLabel}
        captionSuffix=" booked"
        part={second.bookedCount}
        whole={second.count}
        color={lift < 0 ? BOOKED_FILL : BASELINE_FILL}
      />
    </ResultItem>
  )
}

Comparison.propTypes = {
  testId: PropTypes.string.isRequired,
  icon: PropTypes.node.isRequired,
  label: PropTypes.string.isRequired,
  first: groupShape.isRequired,
  second: groupShape.isRequired,
  firstLabel: PropTypes.string.isRequired,
  secondLabel: PropTypes.string.isRequired,
  minSize: PropTypes.number.isRequired,
}

export default function ResultsSection({ results, holdoutPercent }) {
  const classes = useStyles()
  const settingsStyles = useSettingsStyles()
  const {
    periodDays,
    outcomeWindowDays,
    minComparisonSize,
    byTrigger,
    automated,
    heldOut,
    personalized,
    asIs,
  } = results
  const sentRows = byTrigger.filter(row => row.count > 0)

  return (
    <SettingsSection
      eyebrow="Results"
      subtitle={`Follow-ups sent in the last ${periodDays} days. A reply or booking counts as a result if it happens within ${outcomeWindowDays} days.`}
      classes={settingsStyles}
    >
      {sentRows.length === 0 && heldOut.count === 0 ? (
        <Box data-testid="results-empty">
          <SectionEmptyState
            Icon={MonitoringIcon}
            title="Results coming soon"
            caption={`Results show up ${outcomeWindowDays} days after your first follow-ups go out.`}
            captionClassName={classes.resultEmptyCaption}
            bordered={false}
          />
        </Box>
      ) : (
        <>
          {sentRows.map(row => (
            <ResultItem
              key={row.trigger}
              testId={`result-${row.trigger}`}
              icon={RULE_ICONS[row.trigger] ?? MANUAL_ICON}
              title={resultTitle(row.trigger)}
              description={`${row.count} sent`}
              chip={
                <BenchmarkDeltaChip
                  neutral
                  label={`${formatRate(
                    rate(row.bookedCount, row.count),
                  )} booked`}
                />
              }
            >
              <RateBlock
                label="Replied"
                part={row.repliedCount}
                whole={row.count}
                color={REPLIED_FILL}
              />
              <RateBlock
                label="Booked a lesson"
                part={row.bookedCount}
                whole={row.count}
                color={BOOKED_FILL}
              />
            </ResultItem>
          ))}
          <Comparison
            testId="comparison-automated"
            icon={<TrendingUpIcon />}
            label="Automated follow-up vs. none"
            first={automated}
            second={heldOut}
            firstLabel="Got a follow-up"
            secondLabel="Skipped"
            minSize={minComparisonSize}
          />
          <Comparison
            testId="comparison-personalized"
            icon={<EditIcon />}
            label="Personalized vs. template as-is"
            first={personalized}
            second={asIs}
            firstLabel="Personalized"
            secondLabel="Template as-is"
            minSize={minComparisonSize}
          />
        </>
      )}
      <Typography className={classes.resultFootnote}>
        To see whether follow-ups work, automations skip about {holdoutPercent}%
        of matching students at random and compare how often each group books.
      </Typography>
    </SettingsSection>
  )
}

ResultsSection.propTypes = {
  results: PropTypes.shape({
    periodDays: PropTypes.number.isRequired,
    outcomeWindowDays: PropTypes.number.isRequired,
    minComparisonSize: PropTypes.number.isRequired,
    byTrigger: PropTypes.arrayOf(
      PropTypes.shape({
        trigger: PropTypes.string.isRequired,
        count: PropTypes.number.isRequired,
        repliedCount: PropTypes.number.isRequired,
        bookedCount: PropTypes.number.isRequired,
      }),
    ).isRequired,
    automated: groupShape.isRequired,
    heldOut: groupShape.isRequired,
    personalized: groupShape.isRequired,
    asIs: groupShape.isRequired,
  }).isRequired,
  holdoutPercent: PropTypes.number.isRequired,
}
